import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { Dialog } from "@/components/ui/Dialog";
import type { AssetListItem } from "@/lib/api";
import { api } from "@/lib/api";
import {
  VIEWER_KIND_CHOICES,
  bloqueioDaTroca,
  confirmacaoDaTroca,
  descricaoDaEscolha,
  escolhaAtual,
  mensagemDoResultado,
  patchDaTroca,
  resumoDoEstado,
  rotuloDaEscolha,
  type ViewerKindChoice,
} from "@/lib/viewerKindOverride";
import { useFileManagerStore } from "@/state/fileManagerStore";

interface ViewerKindControlProps {
  asset: AssetListItem;
  open: boolean;
  onClose: () => void;
}

/**
 * «Tipo de viewer…» do menu do arquivo (29/09/2026): automático, documento ou
 * embalagem.
 *
 * Mora no card e na linha, e não no `FileManager`, para não precisar de estado
 * na página: a resposta vira patch direto no store da listagem, e o refresh que
 * o WebSocket dispara quando a preparação termina traz o resto. Vai por portal
 * porque o card inteiro é clicável — sem o `stopPropagation` do invólucro, um
 * clique ou um Enter dentro do diálogo selecionaria o card por baixo.
 */
export function ViewerKindControl({ asset, open, onClose }: ViewerKindControlProps) {
  const atual = escolhaAtual(asset);
  const [escolha, setEscolha] = useState<ViewerKindChoice>(atual);
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setEscolha(atual);
    setBusy(false);
    setErro(null);
    setResultado(null);
    // `atual` só na abertura: a listagem pode atualizar o card com o diálogo
    // aberto, e trocar a seleção debaixo do dedo de quem está escolhendo seria pior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const bloqueio = bloqueioDaTroca(asset);
  const confirmacao = confirmacaoDaTroca(asset, escolha);
  const podeConfirmar = !busy && !resultado && !bloqueio && confirmacao !== null && Boolean(asset.current_file_id);

  const confirmar = async () => {
    if (!podeConfirmar || !asset.current_file_id) return;
    setBusy(true);
    setErro(null);
    try {
      const resposta = await api.setViewerKind(asset.current_file_id, escolha);
      useFileManagerStore.getState().updateAsset(asset.id, patchDaTroca(resposta));
      setResultado(mensagemDoResultado(resposta));
    } catch (error) {
      setErro(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  const fechar = busy ? () => {} : onClose;

  const footer = resultado ? (
    <button
      type="button"
      className="rounded bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600"
      onClick={onClose}
    >
      Fechar
    </button>
  ) : (
    <>
      <button
        type="button"
        className="rounded border border-ink-600 px-4 py-2 text-sm text-ink-200 hover:bg-ink-800"
        onClick={onClose}
        disabled={busy}
      >
        Cancelar
      </button>
      <button
        type="button"
        className="rounded bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
        onClick={() => void confirmar()}
        disabled={!podeConfirmar}
      >
        {busy ? "Trocando…" : "Trocar"}
      </button>
    </>
  );

  return createPortal(
    <div
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        // O `Dialog` fecha no Esc pelo `keydown` da `window`, e o
        // `stopPropagation` do React 18 para o evento nativo no contêiner do
        // portal (o `body`): com o foco numa opção, o Esc nunca chegava lá.
        // Fechar aqui, e não deixar o Esc passar, porque passar é chegar ao
        // card por baixo pela árvore do React.
        if (event.key === "Escape") fechar();
        event.stopPropagation();
      }}
    >
      <Dialog title="Tipo de viewer" open={open} onClose={fechar} footer={footer}>
        <p className="mb-3 truncate text-xs text-ink-400" title={asset.path}>
          {asset.filename}
        </p>
        <p className="mb-4 text-ink-200">{resumoDoEstado(asset)}</p>

        <fieldset className="space-y-2" disabled={busy || Boolean(resultado)}>
          <legend className="sr-only">Abrir este arquivo como</legend>
          {VIEWER_KIND_CHOICES.map((opcao) => (
            <label
              key={opcao}
              className={`flex cursor-pointer items-start gap-2 rounded border px-3 py-2 ${
                escolha === opcao
                  ? "border-emerald-600 bg-emerald-950/30"
                  : "border-ink-700 hover:border-ink-500"
              }`}
            >
              <input
                type="radio"
                name={`viewer-kind-${asset.id}`}
                value={opcao}
                checked={escolha === opcao}
                onChange={() => setEscolha(opcao)}
                className="mt-0.5 accent-emerald-600"
              />
              <span className="min-w-0">
                <span className="block text-sm text-ink-100">
                  {rotuloDaEscolha(opcao)}
                  {opcao === atual ? <span className="ml-1.5 text-[10px] text-ink-500">(atual)</span> : null}
                </span>
                <span className="block text-xs text-ink-400">{descricaoDaEscolha(opcao)}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {bloqueio ? (
          <p className="mt-3 rounded border border-amber-900 bg-amber-950/30 px-2 py-1.5 text-xs text-amber-300">
            {bloqueio}
          </p>
        ) : confirmacao && !resultado ? (
          <p className="mt-3 text-xs leading-relaxed text-ink-300">{confirmacao}</p>
        ) : null}

        {resultado ? (
          <p className="mt-3 rounded border border-emerald-900 bg-emerald-950/30 px-2 py-1.5 text-xs text-emerald-300">
            {resultado}
          </p>
        ) : null}

        {erro ? (
          <p className="mt-3 rounded border border-red-900 bg-red-950/40 px-2 py-1.5 text-xs text-red-300">
            {erro}
          </p>
        ) : null}
      </Dialog>
    </div>,
    document.body,
  );
}
